import type { JSX } from 'react';

/**
 * Compact Lucide-style line-icon set for navigation and hubs. Inline SVG (no
 * dependency, no network) so icons ship in the bundle and theme via currentColor.
 */
const PATHS: Record<string, JSX.Element> = {
  today: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  capture: <><path d="M12 5v14M5 12h14" /></>,
  tasks: <><path d="M4 6h16M4 12h16M4 18h10" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>,
  grid: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>,
  calendar: <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></>,
  routine: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 4v4h4" /></>,
  target: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" /></>,
  bell: <><path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6" /><path d="M10 20a2 2 0 0 0 4 0" /></>,
  review: <><path d="M9 11l3 3 8-8" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>,
  focus: <><circle cx="12" cy="12" r="9" /><path d="M12 12 8 8m4 4v5" /></>,
  compass: <><circle cx="12" cy="12" r="9" /><path d="m15.5 8.5-2 5-5 2 2-5 5-2z" /></>,
  roles: <><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="M16 6a3 3 0 0 1 0 6m5 8a6 6 0 0 0-5-5.9" /></>,
  values: <><path d="M12 3l2.5 5.5L20 9l-4 4 1 6-5-3-5 3 1-6-4-4 5.5-.5z" /></>,
  challenge: <><path d="M13 2 4.5 12.5H11l-1 9L19.5 11H13z" /></>,
  problem: <><path d="M12 9v4m0 4h.01" /><path d="M10.3 3.9 2 18a2 2 0 0 0 1.7 3h16.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></>,
  projects: <><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></>,
  knowledge: <><path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" /><path d="M19 17H6a2 2 0 0 0-2 2" /></>,
  notes: <><path d="M5 3h10l4 4v14H5z" /><path d="M15 3v4h4M9 13h6M9 17h6" /></>,
  graph: <><circle cx="6" cy="6" r="2.5" /><circle cx="18" cy="8" r="2.5" /><circle cx="9" cy="18" r="2.5" /><path d="m8 7 8 1M8 16l1-7m7 1-5 6" /></>,
  source: <><path d="M4 5a2 2 0 0 1 2-2h8l6 6v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" /><path d="M14 3v6h6" /></>,
  study: <><path d="M22 10 12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1 2.7 3 6 3s6-2 6-3v-5" /></>,
  career: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
  health: <><path d="M3 12h4l2 5 4-12 2 7h6" /></>,
  finance: <><rect x="2" y="6" width="20" height="13" rx="2" /><circle cx="12" cy="12.5" r="2.5" /><path d="M6 10v5M18 10v5" /></>,
  business: <><path d="M3 21h18M5 21V8l5-3v16M19 21V11l-9-3" /><path d="M9 9v.01M9 13v.01M9 17v.01" /></>,
  work: <><path d="M4 5h16v14H4z" /><path d="M4 9h16M8 5v4" /></>,
  mental: <><path d="M9 4a4 4 0 0 0-4 4 3 3 0 0 0-1 5 3 3 0 0 0 4 4 3 3 0 0 0 4 1V5a3 3 0 0 0-3-1z" /><path d="M15 4a4 4 0 0 1 4 4 3 3 0 0 1 1 5 3 3 0 0 1-4 4 3 3 0 0 1-4 1" /></>,
  spiritual: <><path d="M12 3v18M5 8c0 4 3 6 7 6M19 8c0 4-3 6-7 6" /><circle cx="12" cy="4" r="1.5" /></>,
  environment: <><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></>,
  assistant: <><rect x="4" y="8" width="16" height="11" rx="3" /><path d="M12 8V4M9 13h.01M15 13h.01M2 12v3M22 12v3" /></>,
  runtime: <><path d="M5 12a7 7 0 0 1 14 0" /><path d="M12 12l4-2" /><circle cx="12" cy="12" r="1.4" /><path d="M4 18h16" /></>,
  security: <><path d="M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6z" /><path d="m9 12 2 2 4-4" /></>,
  dot: <><circle cx="12" cy="12" r="3.5" /></>,
};

const ROUTE_ICON: Record<string, string> = {
  '/today': 'today',
  '/capture': 'capture',
  '/tasks': 'tasks',
  '/search': 'search',
  '/all-systems': 'grid',
  '/calendar': 'calendar',
  '/routines': 'routine',
  '/targets': 'target',
  '/reminders': 'bell',
  '/reviews': 'review',
  '/focus': 'focus',
  '/direction': 'compass',
  '/direction/roles': 'roles',
  '/direction/values': 'values',
  '/direction/challenges': 'challenge',
  '/problems': 'problem',
  '/projects': 'projects',
  '/knowledge': 'knowledge',
  '/knowledge/notes': 'notes',
  '/knowledge/graph': 'graph',
  '/source-review': 'source',
  '/study': 'study',
  '/academics': 'study',
  '/career': 'career',
  '/health': 'health',
  '/finance': 'finance',
  '/business': 'business',
  '/work': 'work',
  '/mental': 'mental',
  '/spiritual': 'spiritual',
  '/environment': 'environment',
  '/assistant': 'assistant',
  '/runtime': 'runtime',
  '/security': 'security',
};

export function iconNameForRoute(route: string | undefined): string {
  return (route && ROUTE_ICON[route]) || 'dot';
}

export function Icon({ name, size = 18 }: { name: string; size?: number }): JSX.Element {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      {PATHS[name] ?? PATHS.dot}
    </svg>
  );
}

/** Convenience: the icon for a given nav route. */
export function RouteIcon({ route, size }: { route: string | undefined; size?: number }): JSX.Element {
  return <Icon name={iconNameForRoute(route)} size={size} />;
}
